// What the web asks the API about the routes the 1Office team built for ChannelPlex (their
// reply of 15 October 2026), kept out of services/api.ts so that file is not changed while
// the hiring screens are being reworked:
//
//   - Manage Budgets on the exact opening: the one-time Project-Management link with the
//     budget and the opening to open the page on (B4);
//   - "Move to another store": its review, its confirm, its cities and its check (B3), and
//     their types, for the opening's window (components/hiring/MoveStoreStep.tsx).
//
// Every call goes through the app's own API client (services/api.ts), with its sign-in.
import api from './api';

/* ─────────────────────────────── Manage Budgets on an opening ─────────────────────────────── */

/** The largest budget or opening number (both are INT columns in 1Office). */
export const LAUNCH_ID_MAX = 2_147_483_647;

/** A budget or opening number the launch link may carry: a whole number from 1 to 2147483647. */
export const isLaunchId = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= 1 && value <= LAUNCH_ID_MAX;

/** A budget or opening number from an address: plain digits, 1 to 2147483647; anything else is null. */
export const launchIdFromText = (raw: string | null | undefined): number | null => {
  if (!raw || !/^[1-9]\d{0,9}$/.test(raw)) return null;
  const n = Number(raw);
  return isLaunchId(n) ? n : null;
};

export const manageBudgetsLaunchAPI = {
  /**
   * The one-time link to Manage Budgets, opened on a budget and (when given) one of its
   * openings. POST, as the page's plain link: the answer is a one-time sign-in and must never
   * be cached. The two numbers are the only thing in the body; who the link is for is the
   * server's own answer, exactly as without them.
   */
  requestLink: (p: { projectId: number; budgetId: number; openingId?: number | null }) =>
    api.post(
      `/pm-launch/${p.projectId}/pages/manage-budgets/link`,
      isLaunchId(p.openingId) ? { budgetId: p.budgetId, openingId: p.openingId } : { budgetId: p.budgetId },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      { skipSessionExpiry: true, timeout: 120_000 } as any,
    ),
};

/* ─────────────────────────────── Move to another store ─────────────────────────────── */

/** What a person picks to move an opening: the store, and - when asked - a city and the two ticks. */
export interface HiringMoveInput {
  storeId: string;
  /** A Recruitment city, when the new store's own names did not match one (the review then asks). */
  cityId?: number | null;
  /** "Move although the store is marked …" (a staffing hold). */
  confirmStatus?: boolean;
  /** "<store> needs one more person": the new store already follows another opening. */
  confirmAdditional?: boolean;
}

export interface HiringMoveConfirm extends HiringMoveInput {
  /** Made by the screen when the review arrived; a repeat of the confirm carries the same one. */
  requestKey: string;
  previewHash: string;
}

export interface HiringMoveStore { storeId: string; storeName: string | null; storeCode: string | null }

/** POST /hiring-links/:projectId/links/:linkId/move/preview - the review. Sends nothing. */
export interface HiringMovePreview {
  previewHash: string;
  title: string;
  link: { id: string; openingId: number };
  budget: { id: number; name: string };
  opening: { positions: number; filled: number; offered: number; open: number };
  from: { store: HiringMoveStore; address: { cityName: string | null; location: string | null } };
  to: {
    store: HiringMoveStore;
    address: { cityId: number; cityName: string; stateName: string | null; location: string; pincode: string };
    cityFrom: 'matched' | 'picked';
    distanceKm: number | null;
  };
  /**
   * Recruitment already has the opening at exactly the new store's address (city, address text,
   * map location and PIN code, as 1Office compares them): it will change nothing and tell nobody.
   */
  alreadyThere: boolean;
  /** Everything ChannelPlex could read matches, but not the PIN code: the sentences say what happens either way. */
  maybeAlreadyThere: boolean;
  sentences: string[];
  confirm: { yes: string; no: string; footnote: string };
}

/** How a move, or a check of one, ended (200), or that it is still open (202). */
export type HiringMoveOutcome = 'moved' | 'not_confirmed' | 'in_progress' | 'refused' | 'not_sent' | 'not_moved';

export interface HiringMoveResult {
  outcome: HiringMoveOutcome;
  message: string;
  sendId: string;
  /** The link as the hiring list returns it (the same shape), after the move. */
  link: Record<string, unknown> & { id: string; state: string };
}

/**
 * A move's own refusal codes, beside the hiring link's: the switch (MOVE_NOT_ENABLED), the
 * link (MOVE_NOT_A_STORE, MOVE_SHARED_OPENING), the store (MOVE_SAME_STORE), something
 * already open on the opening (SEND_IN_PROGRESS), Recruitment's own figures
 * (MOVE_HAS_OFFER_OR_JOINER), and a check with nothing to check (LINK_NOT_MOVING).
 */
export type HiringMoveCode =
  | 'MOVE_NOT_ENABLED' | 'MOVE_NOT_A_STORE' | 'MOVE_SHARED_OPENING' | 'MOVE_SAME_STORE' | 'SEND_IN_PROGRESS'
  | 'MOVE_HAS_OFFER_OR_JOINER' | 'LINK_NOT_MOVING';

const unwrap = <T>(res: { data?: { data?: T } }): T => res?.data?.data as T;

export const hiringMoveAPI = {
  preview: async (projectId: number, linkId: string, body: HiringMoveInput): Promise<HiringMovePreview> =>
    unwrap(await api.post(`/hiring-links/${projectId}/links/${encodeURIComponent(linkId)}/move/preview`, body)),
  /** "Yes, move this opening": the one call behind which a move is sent to Recruitment. Once per request key. */
  // It waits as a cancel does: the server's checks, 1Office's own answer and one read back fit well inside it.
  move: async (projectId: number, linkId: string, body: HiringMoveConfirm): Promise<HiringMoveResult> =>
    unwrap(await api.post(`/hiring-links/${projectId}/links/${encodeURIComponent(linkId)}/move`, body, { timeout: 120_000 })),
  /** Recruitment's cities, when the review asked the person to pick the new store's (CITY_NOT_MATCHED / CITY_MATCHED_SEVERAL). */
  cities: async (projectId: number, linkId: string, q: string): Promise<{ cities: Array<{ cityId: number; cityName: string; stateName: string; hasMapLocation: boolean }> }> =>
    unwrap(await api.get(`/hiring-links/${projectId}/links/${encodeURIComponent(linkId)}/move/cities`, { params: { q } })),
  /** A move Recruitment did not confirm: where the opening is, read again. Never sends. */
  check: async (projectId: number, linkId: string): Promise<HiringMoveResult> =>
    unwrap(await api.post(`/hiring-links/${projectId}/links/${encodeURIComponent(linkId)}/move/check`, {})),
};
